// @ts-strict-ignore
/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CurrentVersion } from 'src/app/models/current-version.model';
import { EnvironmentEnum } from 'src/app/models/environment-enum';
import { AdminService } from 'src/app/services/admin.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { Benchmark } from '../../models/rates.model';
import { Tenor } from '../../models/roll-dates.model';
import { User } from '../../models/user';
import { PoolService } from '../../services/pool.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { SessionService } from '../../services/session.service';
import { ConfirmDialogComponent } from '../common/confirm-dialog/confirm.dialog';

@Component({
  selector: 'side-nav',
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss',
})
export class TopNavComponent implements OnInit, OnDestroy {
  public user: User;
  public userName: string;
  public createdAt: string;
  public isFXHPAdmin: boolean;
  public isFXHPViewer: boolean;
  public isFXHPRiskManager: boolean;
  public isAdmin: boolean;
  public hasAuditAccess: boolean;
  public isCreditRiskManager: boolean;
  public isTrader: boolean;
  public isCreditBank: boolean;
  public showIsProd: boolean;
  public pageTitle: string;
  public environmentName: string;
  public localBuildNumber: string;
  public localLastUpdated: string;
  public currentVersion: CurrentVersion = {
    lastUpdated: null,
    buildNumber: null,
  };
  private pageClosed = new Subject<void>();
  public entityName: string;
  public rolesDisplayString: string;
  public accessibility: boolean = false;
  public showAccessibilityButton = false;
  public selectedEntityId: string;
  public isNonFXHPAdmin: boolean;
  public isBlotterTrader: boolean;
  public isFixingOrderTrader: boolean;
  public userInitials = '';
  private readonly dialog: MatDialog = inject(MatDialog);

  @HostListener('window:focus', ['$event']) public onFocus(): void {
    this.sessionService.checkToken().subscribe();
  }

  constructor(
    private readonly adminService: AdminService,
    private readonly analyticsService: AnalyticsService,
    private readonly sessionService: SessionService,
    private readonly userService: UserService,
    private readonly localStorageService: LocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly poolService: PoolService,
    public notificationsService: NotificationsService,
  ) {}

  public ngOnInit(): void {
    this.sessionService.setupTokenRefreshTimer();
    this.isFXHPAdmin = this.sessionService.isFXHPAdmin();
    this.isFXHPViewer = this.sessionService.isFXHPViewer();
    this.isFXHPRiskManager = this.sessionService.isFXHPRiskManager();
    this.isAdmin = this.sessionService.isAdmin();
    this.hasAuditAccess = this.sessionService.canAccessAudit();
    this.isCreditBank = this.sessionService.isCreditBank();
    this.entityName = this.isFXHPAdmin
      ? this.sessionService.getJWTPayload()['custom:entityType'].toUpperCase()
      : this.sessionStorageService.get('entityName');
    this.getUserDetails();
    this.environmentName = environment.name;
    this.showIsProd = environment.name === EnvironmentEnum.Prod;
    this.isCreditRiskManager =
      this.sessionService.isRiskManager() && this.isCreditBank;
    this.isNonFXHPAdmin = this.sessionService.isNonFXHPAdmin();
    this.isTrader = this.sessionService.isTrader();
    this.isBlotterTrader = this.sessionService.isBlotterTrader();
    this.isFixingOrderTrader = this.sessionService.isFixingOrderTrader();

    this.sessionStorageService
      .watchStorage()
      .pipe(takeUntil(this.pageClosed))
      .subscribe(() => {
        this.entityName = this.sessionStorageService.get('entityName');
      });
    if (this.sessionService.isCreditBank()) {
      this.showAccessibilityButton = true;
      if (JSON.parse(localStorage.getItem('accessibility'))) {
        this.accessibility = JSON.parse(localStorage.getItem('accessibility'));
      }
    } else {
      localStorage.setItem('accessibility', 'false');
    }
    this.localBuildNumber = this.localStorageService.get('appVersion');
    this.localLastUpdated = this.localStorageService.get('lastUpdated');

    //set the tenors on the app
    this.adminService
      .fetchAllTenors()
      .pipe(takeUntil(this.pageClosed))
      .subscribe((results: Tenor[]) => {
        if (results && results.length > 0) {
          this.poolService.setAllAvailableTenors(results);
        }
      });
    //set the benchmarks on the app
    this.adminService
      .fetchAllBenchmarkss()
      .pipe(takeUntil(this.pageClosed))
      .subscribe((results: Benchmark[]) => {
        if (results && results.length > 0) {
          this.poolService.setAllAvailableBenchmarks(results);
        }
      });
  }

  public ngOnDestroy(): void {
    this.pageClosed.next();
    this.pageClosed.complete();
  }
  public getUserDetails(): void {
    this.userService.user$
      .pipe(
        filter((user) => user != null),
        takeUntil(this.pageClosed),
      )
      .subscribe({
        next: (user: User) => {
          this.user = user;
          this.userName = user.name;
          this.createdAt = user.createdAt;
          this.entityName = this.isFXHPAdmin
            ? this.sessionService
                .getJWTPayload()
                ['custom:entityType'].toUpperCase()
            : user.roles[0].entity.name;
          this.sessionStorageService.set('entityName', this.entityName);
          this.analyticsService.sendEventAnalytics(
            'get current user',
            'successful',
          );
          if (user.active === 0 || user.active === 2) {
            this.setUserActive();
          }
          this.createDisplayRoles(this.user);
          this.userInitials = this.userName
            .match(/(^\S\S?|\b\S)?/g)
            .join('')
            .match(/(^\S|\S$)?/g)
            .join('')
            .toUpperCase();
        },
        error: () => {
          this.analyticsService.sendEventAnalytics(
            'get current user',
            'failed',
          );
        },
      });
  }

  private createDisplayRoles(user: User): string {
    const roles = new Set();
    this.rolesDisplayString = '';
    if (user && user.roles && user.roles.length > 0) {
      user.roles.forEach((role) => {
        roles.add(role.role.name);
      });
      this.rolesDisplayString = Array.from(roles).join(', ');
    }
    return this.rolesDisplayString;
  }

  public setUserActive(): void {
    this.userService
      .setUserActive()
      .pipe(takeUntil(this.pageClosed))
      .subscribe({
        next: () => {
          this.analyticsService.sendEventAnalytics(
            'set user active',
            'successful',
          );
        },
        error: () => {
          this.analyticsService.sendEventAnalytics('set user active', 'failed');
        },
      });
  }

  public openLogOutDialog(): void {
    const logOutDialog = this.dialog.open(ConfirmDialogComponent, {
      width: '407px',
      height: 'auto',
      data: {
        title: 'Log Out',
        message: 'Are you sure you want to log out?',
      },
    });

    logOutDialog
      .afterClosed()
      .pipe(takeUntil(this.pageClosed))
      .subscribe((res) => {
        if (res) {
          this.adminService.clearParties();
          this.sessionService.logout();
        }
      });
  }

  public setTheme(): void {
    this.accessibility = !this.accessibility;
    localStorage.setItem('accessibility', this.accessibility.toString());
  }
}
